import { Order } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  ContentBox,
  LoadingSkeleton,
  OrderContentList,
  OrderDetailBasicInfo,
  OrderSummary,
  StatusLight,
  Title,
} from '@components';
import { Box, Grid, styled } from '@mui/material';
import { Stack } from '@mui/system';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations/translation';
import { fullDateAndTime } from '@utils/date.utils';

const OrderHeaderWrapper = styled(Box)`
  display: flex;
  padding-bottom: 50px;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  border-radius: 4px;
  ${breakpoints.up('lg')} {
    flex-direction: row;
    align-items: center;
    align-content: center;
  }
`;

const OrderHeaderInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  & > div {
    display: flex;
    gap: 16px;
  }
  ${breakpoints.up('md')} {
    flex-direction: row;
  }
  ${breakpoints.up('lg')} {
    justify-content: flex-end;
    align-items: center;
    align-content: center;
    margin-left: auto;
  }
`;

export type OrderDetailsProps = {
  order?: Order;
};

export const OrderDetails = ({ order }: OrderDetailsProps) => {
  const quantities = order?.shipments
    ?.flatMap((shipment) => shipment?.items?.map((item) => item.quantity ?? 0))
    .reduce((acc, quantity) => acc + quantity, 0);

  // Main title text - get translation with Visma token
  const orderTitle = t(TextCodes.Order.Details.OrderDetailsHeader, {
    orderNumber: order?.vismaOrderNumber || '',
  });
  // Created by text - get translation with date
  const createdTitle = t(TextCodes.Order.Details.Created, {
    createdOn: fullDateAndTime(order?.createdOn),
  });

  return (
    <div>
      <OrderHeaderWrapper>
        <Title variant='h2'>{orderTitle}</Title>
        <OrderHeaderInfo>
          <div>
            {order?.erpStatus && (
              <StatusLight status={order?.erpStatus}>{order?.erpStatus}</StatusLight>
            )}
          </div>

          <div>
            {createdTitle} {order?.createdBy || 'Unknown'}
          </div>
        </OrderHeaderInfo>
      </OrderHeaderWrapper>

      <Stack direction='column' gap={{ xs: 2, md: 4 }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          <Grid item xs={12} md={7} lg={8}>
            <OrderDetailBasicInfo order={order} />
          </Grid>
          <Grid item xs={12} md={5} lg={4}>
            <OrderSummary
              title={t(TextCodes.Order.Details.OrderSummaryHeader)}
              delivery={order?.shippingTotal}
              itemCount={quantities || 0}
              subtotal={order?.subTotal}
              tax={order?.taxTotal}
              total={order?.total}
            />
          </Grid>
        </Grid>
        {order?.comment && (
          <ContentBox title={t(TextCodes.Order.Details.OrderCommentsHeader)}>
            {order.comment}
          </ContentBox>
        )}
        {order?.shipments?.map(
          ({ items, expectedDeliveryDate, trackingNumber, name, trackingLink }, index) => (
            <OrderContentList
              key={index}
              items={items}
              expectedDeliveryDate={expectedDeliveryDate}
              trackingNumber={trackingNumber}
              shipmentName={name}
              trackingLink={trackingLink}
            />
          )
        )}
      </Stack>
    </div>
  );
};

export const OrderDetailsSkeleton = () => {
  return (
    <div>
      <OrderHeaderWrapper>
        <Title variant='h2' noDot sx={{ width: '100%' }}>
          <LoadingSkeleton sx={{ width: '100%' }} />
        </Title>
      </OrderHeaderWrapper>
      <Stack direction='column' gap={{ xs: 2, md: 4 }} sx={{ height: '200px' }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          <Grid item xs={12} md={8}>
            <LoadingSkeleton sx={{ height: '200px' }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <LoadingSkeleton sx={{ height: '200px' }} />
          </Grid>
        </Grid>
      </Stack>
    </div>
  );
};
